import {
  AccountData,
  Campaign,
  DMAutomation,
  DMAutomationState,
  Event,
  MassText,
  MassTextAttachment,
  MassTextAttachmentType,
} from "@markit/common.types";
import {
  CampaignDetails,
  MassTextDetails,
} from "../redux/slices/campaignSlice";
import {
  getCampaignMassTextData,
  getCampaignMassTextsRef,
  getCampaignMassTextsSnap,
  getUserCampaignsRef,
} from "./FirebaseUtils";
import {
  getMassTextAttachments,
  getNumMassTextRecipients,
} from "./textingUtils";
import {
  SUBSCRIBED_CAMPAIGN_NAME,
  LessThanDate,
  NON_SUBSCRIBER_REPLY_CAMPAIGN_ID,
  isDelayedTriggerAutomation,
  isEventRelatedType,
  isDefaultPresetAutomations,
} from "@markit/common.utils";
import { getCountFromServer, getDocs, limit, query, where } from "../firebase";
import { API } from "../API";
import { showTextConversions } from "./trackingUtils";
import { fetchSingleCampaign } from "./FetchSingleData";

export const fetchCampaignDetails = async (
  userId: string,
  campaign: Campaign
): Promise<CampaignDetails> => {
  const massTextsSnap = await getCampaignMassTextsSnap(userId, campaign.id);
  const numRecipients = await getNumMassTextRecipients(
    userId,
    campaign,
    campaign.initialTextId
  );
  const massTextDetails: MassTextDetails[] = await Promise.all(
    massTextsSnap.docs.map(async (doc) => {
      const massText = doc.data();
      const attachments = await getMassTextAttachments(userId, massText);
      return {
        massText,
        attachments,
      };
    })
  );
  return { campaignId: campaign.id, numRecipients, massTextDetails };
};

// Fetches the initial mass text for a campaign and it's attachments
export const fetchCampaignInitialMassText = async (
  userId: string,
  campaignId: string,
  campaigns: Campaign[]
): Promise<
  { massText: MassText; attachments: MassTextAttachment[] } | undefined
> => {
  const campaign = await fetchSingleCampaign(userId, campaignId, campaigns);
  if (campaign && campaign.initialTextId) {
    const massText = await getCampaignMassTextData(
      userId,
      campaignId,
      campaign.initialTextId
    );
    if (massText) {
      const attachments = await getMassTextAttachments(userId, massText);
      return { massText, attachments };
    }
  }
};

// Fetched the campaign associated with an event
export const fetchEventCampaignData = async (
  userId: string,
  eventId: string
): Promise<Campaign | undefined> => {
  const campaignsRef = getUserCampaignsRef(userId);
  const query_ = query(campaignsRef, where("eventId", "==", eventId), limit(1));
  const snapshot = await getDocs(query_);
  if (!snapshot.empty) {
    const campaign = snapshot.docs.map((doc) => doc.data())[0];
    return campaign;
  }
};

// Fetches the subscribe campaign and the campaign details
export const fetchSubscribeCampaignData = async (
  userId: string
): Promise<{
  campaign: Campaign | undefined;
  campaignDetails: CampaignDetails | undefined;
}> => {
  const campaignsRef = getUserCampaignsRef(userId);
  const query_ = query(
    campaignsRef,
    where("title", "==", SUBSCRIBED_CAMPAIGN_NAME),
    limit(1)
  );
  const snapshot = await getDocs(query_);
  if (!snapshot.empty) {
    const subscribeCampaign = snapshot.docs.map((doc) => doc.data())[0];
    const campaignDetails = await fetchCampaignDetails(
      userId,
      subscribeCampaign
    );
    return { campaign: subscribeCampaign, campaignDetails };
  }
  return { campaign: undefined, campaignDetails: undefined };
};

// Fetches the non subscriber reply campaign and the campaign details
export const fetchNonSubscriberReplyCampaign = async (
  userId: string
): Promise<{
  campaign: Campaign | undefined;
  campaignDetails: CampaignDetails | undefined;
}> => {
  const campaignsRef = getUserCampaignsRef(userId);
  const query_ = query(
    campaignsRef,
    where("id", "==", NON_SUBSCRIBER_REPLY_CAMPAIGN_ID),
    limit(1)
  );
  const snapshot = await getDocs(query_);
  if (!snapshot.empty) {
    const campaign = snapshot.docs.map((doc) => doc.data())[0];
    const campaignDetails = await fetchCampaignDetails(userId, campaign);
    return { campaign, campaignDetails };
  }
  return { campaign: undefined, campaignDetails: undefined };
};

// Determines whether to show the link clicks and conversions
export const displayTextAnalytics = (attachments: MassTextAttachment[]) => {
  return (
    attachments.length > 1 ||
    (attachments.length === 1 &&
      attachments[0].massTextType !== MassTextAttachmentType.TICKET_LINK &&
      attachments[0].massTextType !== MassTextAttachmentType.CALENDAR_INVITE)
  );
};

// Determines whether to show the link conversions when displaying the analytics
// Use this logic if the event link being shared on the message is an external link, then hide conversions
export const displayTextConversions = (
  attachments: MassTextAttachment[],
  events: Event[]
) => {
  // If no attachments, show conversions
  if (attachments.length === 0) {
    return false;
  }

  // Check if any attachments are for Markit events
  const hasConversions = attachments.some((attachment) => {
    const foundEvent = events.find((event) => event.id === attachment.eventId);
    return foundEvent && showTextConversions(foundEvent, attachment);
  });

  return hasConversions;
};

// Creates the subscribe campaign on app load, if it doesn't exist already
// TODO: Eventually move this logic so it isn't called on each app load and instead created on account creation or something
export const createSubscribeTriggerCampaign = async (userData: AccountData) => {
  try {
    await API.campaign.createSubscribeTriggerCampaign({ userData });
  } catch (error) {
    console.error("Error creating subscribe trigger campaign", error);
  }
};

// Determines if the automation campaign is Active
// If it's default created automation, last automation text within a day of sending, event end still upcoming
export const isAutomationActive = (
  campaign: Campaign,
  massTexts: MassText[],
  foundEvent: Event | undefined,
  dmAutomation?: DMAutomation
) => {
  // Ignore default preset automations, these fall within other tabs
  if (isDefaultPresetAutomations(campaign)) {
    return false;
  }
  if (!isDelayedTriggerAutomation(campaign, foundEvent)) {
    const lastMassText = [...massTexts].sort(
      (a, b) => new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime()
    )[0];
    const oneDayAfter = new Date(lastMassText.sentAt);
    oneDayAfter.setDate(oneDayAfter.getDate() + 1);
    // Checks if current date is less than one day prior to the last mass text sent
    if (LessThanDate(new Date().toISOString(), oneDayAfter.toISOString())) {
      return true;
    }
  }
  if (dmAutomation) {
    return dmAutomation.state === DMAutomationState.ACTIVE;
  } else if (campaign.dmAutomationId) {
    // If no dmAutomation provided but it has dmAutomationId (AutomationsListPanel), we just return true for this case.
    // Not perfect, but it would require us to fetch the dmAutomation to check if it's active.
    return true;
  }
  if (foundEvent) {
    if (isEventRelatedType(foundEvent.eventType)) {
      // Checks if event trigger campaign and the event end is still upcoming
      return LessThanDate(new Date().toISOString(), foundEvent.end);
    } else {
      // If the form is marked as inactive
      return !foundEvent.isManualSoldOut;
    }
  }
  return false;
};

// Checks if the offset of a trigger text is the same as an existing trigger text
export const checkIfTriggerTextOffsetExists = async (
  userId: string,
  campaignId: string,
  offset: number,
  massTextId: string // if editing a mass text, we need to exclude this from the query
) => {
  const massTextsRef = getCampaignMassTextsRef(userId, campaignId);
  const query_ = query(
    massTextsRef,
    where("sentAtOffset", "==", offset),
    where("id", "!=", massTextId)
  );
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count > 0;
};

// Checks if an automation exists for an event/product
export const checkIfAutomationExists = async (
  userId: string,
  eventId: string
) => {
  const campaignsRef = getUserCampaignsRef(userId);
  const query_ = query(campaignsRef, where("eventId", "==", eventId), limit(1));
  const snapshot = await getCountFromServer(query_);
  return snapshot.data().count > 0;
};
